/* eslint-disable id-length */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import Legend from './Legend/legend';
import Chart from '../../../../Components/Chart/chart';
import styles from './graphView.module.scss';

const LABEL_HOVER_TOP_OFFSET = 12;
const LABEL_HOVER_LEFT_OFFSET = 12;
const LABEL_HOVER_REGION_HEIGHT = 140;

const GraphView = ({ projectDetails }) => {
  const [fontSize, setFontSize] = useState(14);
  const [hoveredLabel, setHoveredLabel] = useState(null);

  if (!projectDetails?.overviewColumns || !projectDetails.overviewData) {
    return null;
  }

  const xAxisLabels = projectDetails.overviewColumns
    .map((column) => column.field)
    .filter((field) => field !== 'status');

  const statusData = {};

  const sortedStatuses = Array.isArray(projectDetails.overviewData) //  sort statuses by displayOrder
    ? projectDetails.overviewData
      .sort((status1, status2) => Number(status1.displayOrder) - Number(status2.displayOrder))
    : [];

  for (const data of sortedStatuses) {
    const categoryValues = [];
    for (const key in data) {
      if (key === 'id' || key === 'status' || key === 'statusColour' || key === 'displayOrder') {
        continue;
      }

      categoryValues.push({
        name: key,
        value: data[key],
        x: key,
        y: data[key].split(' ')[0].split('%')[0], // extracting just the numeric part
      });
    }
    statusData[data.status] = {
      data: categoryValues,
      color: data.statusColour,
    };
  }

  const datasets = [];

  Object.keys(statusData).forEach((status) => {
    const datasetObj = {
      label: status,
      data: statusData[status].data,
      backgroundColor: statusData[status].color,
      barThickness: 15.06,
      stack: 'stack1',
      borderColor: 'transparent',
      borderWidth: 1,
    };
    datasets.push(datasetObj);
  });


  const chartData = {
    labels: xAxisLabels,
    datasets,
  };

  const handleChartMouseMove = ({ event, chart }) => {
    const xScale = chart?.scales?.x;
    const labels = chartData.labels || [];
    const hoverTarget = event.currentTarget;

    if (!xScale || labels.length === 0 || !hoverTarget) {
      setHoveredLabel(null);
      return;
    }

    const rect = hoverTarget.getBoundingClientRect();
    const pointerX = event.clientX - rect.left;
    const pointerY = event.clientY - rect.top;
    const hoverAreaTop = Math.max(xScale.top, rect.height - LABEL_HOVER_REGION_HEIGHT);
    const hoverAreaBottom = rect.height;
    const isInsideLabelBand = pointerX >= xScale.left
      && pointerX <= xScale.right
      && pointerY >= hoverAreaTop
      && pointerY <= hoverAreaBottom;

    if (!isInsideLabelBand) {
      hoverTarget.style.cursor = 'default';
      setHoveredLabel(null);
      return;
    }

    let labelIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    labels.forEach((_, index) => {
      const tickX = xScale.getPixelForTick(index);
      const distance = Math.abs(pointerX - tickX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        labelIndex = index;
      }
    });

    const labelText = labels[labelIndex];

    if (!labelText) {
      hoverTarget.style.cursor = 'default';
      setHoveredLabel(null);
      return;
    }

    hoverTarget.style.cursor = 'default';
    setHoveredLabel({
      text: labelText,
      left: event.clientX + LABEL_HOVER_LEFT_OFFSET,
      top: event.clientY + LABEL_HOVER_TOP_OFFSET,
    });
  };

  const chartOptions = {
    responsive: true,
    animation: {
      duration: 500,
      easing: 'easeInOutQuad',
    },
    'plugins.tooltip': {
      backgroundColor: '#FFFFFF',
      bodyColor: '#49454E',
      titleColor: '#49454E',
      borderColor: '#DDD',
      borderWidth: 1,
      position: 'point',
      displayColors: false,
      titleFont: {
        family: 'Mukta',
        weight: 600,
        size: fontSize, // in px
      },
      bodyFont: {
        family: 'Mukta',
        weight: 400,
        size: fontSize, // in px
      },
      callbacks: {
        label: function (context) {
          return `${context.dataset.label} - ${context.raw.value}`; // Just return the y-value
        },
        title: function (tooltipItems) {
          return tooltipItems[0].label;
        },
      },
    },
    'scales.x': {
      stacked: true,
      grid: {
        display: false,
      },
      ticks: {
        color: '#7A757F',
        callback: (value) => {
          const label = chartData.labels[value] || value; // Ensure the label is displayed correctly
          return label.length > 20 ? label.slice(0, 20) + '…' : label;
        },
        font: {
          family: 'Mukta',
          weight: 400,
          size: fontSize, // in px
        },
        maxRotation: 45,
        minRotation: 45,
      },
    },
    'scales.y': {
      min: 0,
      max: 130,
      display: false,
    },
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setFontSize(width > 1600 ? 16 : 14);
    };
    handleResize(); // Set initial font size based on current window width
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Legend datasets={datasets} />
      <div className={styles['graph-container']}>
        <Chart
          chartType="bar"
          data={chartData}
          customOptions={chartOptions}
          containerClass={styles.container}
          onChartMouseMove={handleChartMouseMove}
          onMouseLeave={(event) => {
            if (event?.currentTarget) {
              event.currentTarget.style.cursor = 'default';
            }
            setHoveredLabel(null);
          }}
        />
        {hoveredLabel && (
          <div
            className={styles['axis-hover-tooltip']}
            style={{
              left: `${hoveredLabel.left}px`,
              top: `${hoveredLabel.top}px`,
            }}
          >
            {hoveredLabel.text}
          </div>
        )}
      </div>
    </>
  );
};

GraphView.propTypes = {
  projectDetails: PropTypes.shape({
    overviewColumns: PropTypes.array.isRequired,
    overviewData: PropTypes.array.isRequired,
  }).isRequired,
};

export default GraphView;
